'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

import { RecentSearchChips } from './RecentSearchChips';
import { SearchBar } from './SearchBar';

interface HeaderSearchOverlayProps {
  isOpen: boolean;
  keyword?: string;
  onClose: () => void;
  onSearch: (keyword: string) => void;
  recentKeywords: string[];
  onSelectRecent: (keyword: string) => void;
  onRemoveRecent: (keyword: string) => void;
  onClearRecent: () => void;
}

export function HeaderSearchOverlay({
  isOpen,
  keyword,
  onClose,
  onSearch,
  recentKeywords,
  onSelectRecent,
  onRemoveRecent,
  onClearRecent,
}: HeaderSearchOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleUserScroll = () => onClose();

    window.addEventListener('wheel', handleUserScroll, { passive: true });
    window.addEventListener('touchmove', handleUserScroll, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleUserScroll);
      window.removeEventListener('touchmove', handleUserScroll);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;

      // header 내부 클릭은 통과
      const header = overlayRef.current?.closest('header');
      if (header?.contains(target)) return;

      // Radix Dropdown/Select/Combobox/Dialog 등은 Portal로 body 직속에 렌더됨.
      // 오버레이 내 컴포넌트의 Portal 콘텐츠 클릭은 "외부 클릭"으로 취급하지 않음.
      if (
        target.closest(
          '[role="menu"],[role="listbox"],[role="dialog"],[data-radix-popper-content-wrapper]',
        )
      ) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          key="header-search-overlay"
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-x-0 top-full z-10 overflow-hidden border-b border-Gray-200 bg-Gray-white"
        >
          <div className="mx-auto flex max-w-screen-xl flex-col items-center gap-12 px-40 pb-20 pt-12">
            <SearchBar
              keyword={keyword}
              shouldAutoFocus={isOpen}
              sticky={false}
              onSearch={onSearch}
            />
            {recentKeywords.length > 0 && (
              <RecentSearchChips
                keywords={recentKeywords}
                onSelect={onSelectRecent}
                onRemove={onRemoveRecent}
                onClearAll={onClearRecent}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
